import { cn } from "@/lib/utils";
import { formatBucketLabel } from "./lib/bucket-label";
import type { ReportBucket, ReportGroupBy, ReportStage } from "@/types/production-report.types";

// Spec 083 (T035) — o indicador principal por agrupamento, contra a meta.
//
// Colunas com CSS var, não biblioteca: o gráfico tem uma série e uma linha de referência, e trazer
// uma dependência de gráfico para isso custaria mais em bundle e em orçamento de CSS do que entrega.
//
// ⚠️ Altura e posição vêm de `style={{ "--x": … }}` consumido por classe arbitrária. Template
// literal dentro da classe (`h-[${n}%]`) é proibido: o extractor do Tailwind não o enxerga em tempo
// de build e a barra nasce sem altura em produção, mas certinha em dev.

interface Props {
  stage: ReportStage;
  heroId: string | null;
  groupBy: ReportGroupBy;
}

const pct = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

interface Coluna {
  bucket: ReportBucket;
  value: number | null;
}

/**
 * Escala com folga acima do maior valor, e nunca abaixo da meta: a meta tem que caber. A folga é o
 * espaço do rótulo que fica em cima da coluna mais alta (~20 px de 160).
 */
function escala(colunas: Coluna[], target: number | null): number {
  const maior = Math.max(...colunas.map((c) => c.value ?? 0), target ?? 0, 1);
  return maior * 1.15;
}

export function IndicatorChart({ stage, heroId, groupBy }: Props) {
  const hero = stage.indicators.find((i) => i.id === heroId) ?? stage.indicators[0];
  if (!hero) return null;

  const colunas: Coluna[] = stage.buckets.map((b) => ({
    bucket: b,
    value: b.indicators.find((i) => i.id === hero.id)?.value ?? null,
  }));
  if (colunas.every((c) => c.value === null)) return null;

  const teto = escala(colunas, hero.target);
  const metaPct = hero.target === null ? null : (hero.target / teto) * 100;

  return (
    <figure className="space-y-3">
      <figcaption className="flex items-baseline justify-between">
        <h3 className="text-[13px] font-medium text-text-primary">{hero.name} por período</h3>
        {hero.target !== null ? (
          <span className="text-[11px] text-text-tertiary">meta {pct.format(hero.target)}%</span>
        ) : null}
      </figcaption>

      <div className="relative flex h-40 items-end gap-2 border-b border-border-subtle pb-0">
        {metaPct !== null ? (
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-[var(--meta)] z-10 border-t border-dashed border-border-strong"
            style={{ "--meta": `${metaPct}%` } as React.CSSProperties}
          >
            {/* A linha tracejada sem número é um traço no meio do gráfico: quem chega na tela não
                sabe se é meta, média ou período anterior. O rótulo mora NA linha, não só na
                legenda do topo, porque é ali que o olho compara. */}
            <span className="absolute -top-2 right-0 bg-bg-surface pl-1 font-mono text-[10px] tabular-nums text-text-tertiary">
              {pct.format(hero.target ?? 0)}%
            </span>
          </div>
        ) : null}
        {colunas.map((c) => {
          const altura = c.value === null ? 0 : (c.value / teto) * 100;
          const abaixo = hero.target !== null && c.value !== null && c.value < hero.target;
          // Sem meta não há veredito: a coluna fica na cor da marca, que não afirma nada.
          const tom =
            hero.target === null
              ? "border-brand-500 bg-brand-50"
              : abaixo
                ? "border-rd-warning-500 bg-rd-warning-50"
                : "border-rd-success-500 bg-rd-success-50";
          // ⚠️ A coluna ocupa a altura INTEIRA do gráfico (`h-full`) e a barra mede % dela. Sem
          // isso a coluna tem a altura do conteúdo, o `%` da barra resolve contra nada e sobra só
          // o topo de 3 px — um traço no chão para todo período. O rótulo flutua em cima da barra
          // (`bottom-full`) para não roubar altura dela: com ele no fluxo, a barra e a linha da
          // meta mediriam contra alturas diferentes.
          return (
            <div key={c.bucket.key} className="flex h-full min-w-0 flex-1 items-end">
              <div
                className={cn(
                  "relative h-[var(--h)] w-full rounded-t-sm",
                  "motion-safe:transition-[height] motion-safe:[transition-duration:280ms]",
                  c.value !== null && ["border-t-[3px]", tom]
                )}
                style={{ "--h": `${altura}%` } as React.CSSProperties}
                title={`${formatBucketLabel(c.bucket, groupBy)}: ${
                  c.value === null ? "sem base" : `${pct.format(c.value)}%`
                }`}
              >
                <span
                  className={cn(
                    "absolute inset-x-0 bottom-full mb-1 text-center",
                    "font-mono text-[10px] font-medium tabular-nums",
                    hero.target === null || c.value === null
                      ? "text-text-secondary"
                      : abaixo
                        ? "text-rd-warning-700"
                        : "text-rd-success-700"
                  )}
                >
                  {c.value === null ? "—" : `${pct.format(c.value)}%`}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        {colunas.map((c) => (
          <span
            key={c.bucket.key}
            className="min-w-0 flex-1 truncate text-center text-[10px] text-text-tertiary"
          >
            {formatBucketLabel(c.bucket, groupBy)}
            {c.bucket.partial ? " ·parcial" : ""}
          </span>
        ))}
      </div>
    </figure>
  );
}
